<template>
  <card dis-hover :padding="8" class="pc-created">
    <div class="pc-created-content">
      <divider orientation="left"
        >Общая информация о проекте и контрагентах</divider
      >
      <div class="pc-created-row">
        <div class="pc-created-cell">
          <span class="ms-help label"> Проект </span>
          <i-select
            @on-change="getContractData"
            size="default"
            v-model="payment.contract_id"
            :disabled="!!payment.id"
            transfer-class-name="pc-created-companies-dropdown"
            filterable
          >
            <i-option
              v-for="contract in contracts"
              :value="contract.id"
              :key="contract.id"
              :label="`${contract.code}. ${contract.name}`"
            >
              <b>{{ contract.code }} (договор №{{ contract.number }})</b>.
              {{ contract.name }}
            </i-option>
          </i-select>
        </div>
      </div>

      <div class="pc-created-row" v-if="payment.contract_id">
        <div class="pc-created-cell">
          <span class="ms-help label"> Отправитель </span>
          <i-select size="default" v-model="payment.from_company_id" filterable>
            <i-option
              v-for="company in companies"
              :value="company.id"
              :key="company.id"
              :label="company.name"
            >
              {{ company.name }}
            </i-option>
          </i-select>
        </div>
        <div class="pc-created-cell">
          <span class="ms-help label"> Получатель </span>
          <i-select size="default" v-model="payment.to_company_id" filterable>
            <i-option
              v-for="company in companies"
              :value="company.id"
              :key="company.id"
              :label="company.name"
            >
              {{ company.name }}
            </i-option>
          </i-select>
        </div>
      </div>

      <div class="pc-created-details" v-if="commonInfoFilled">
        <divider orientation="left">Основные сведения</divider>
        <div class="pc-created-row">
          <div class="pc-created-cell">
            <span class="ms-help label"> Тип платежа </span>
            <i-select size="default" v-model="payment.type_id">
              <i-option
                v-for="type in types"
                :value="type.id"
                :key="type.id"
                :label="type.name"
              >
                {{ type.name }}
              </i-option>
            </i-select>
          </div>
        </div>

        <div class="pc-created-row" v-if="payment.type_id !== 2">
          <div class="pc-created-cell">
            <span class="ms-help label"> Акт </span>
            <i-select
              size="default"
              v-model="payment.act_id"
              placeholder="Выберите акт"
            >
              <i-option
                v-for="act in acts"
                :value="act.id"
                :key="act.id"
                :label="getActLabel(act)"
              >
                <span> Акт №{{ act.number }} </span>
                <span class="pc-created-act-option ms-help">
                  (оплачено {{ getFormattedCosts(act.payments, true, true) }} /
                  {{ getFormattedCosts(act.costs, true, true) }} )
                </span>
              </i-option>
            </i-select>
          </div>
        </div>

        <div class="pc-created-row">
          <div class="pc-created-cell">
            <payments-editor-roadmap
              v-show="payment.type_id === 2 || payment.act_id"
              :acts="acts"
              :roadmap="roadmap"
              :payment="payment"
              :payment-modal="!!payment"
              :current-max-value="currentMaxValue"
            />
          </div>
        </div>

        <div class="pc-created-row" v-if="payment.type_id === 3">
          <div class="pc-created-cell">
            <span class="ms-help label"> Описание платежа </span>
            <i-input
              v-model="payment.description"
              type="textarea"
              placeholder="Например, платеж за проживание"
            />
          </div>
        </div>
      </div>

      <div class="pc-created-subinfo" v-if="commonInfoFilled">
        <divider orientation="left">Сведения о платеже</divider>

        <div class="pc-created-row">
          <div class="pc-created-cell">
            <span class="ms-help label"> Сумма платежа </span>
            <input-number
              size="default"
              v-model="payment.value"
              :min="0"
              :max="currentMaxValue"
              placeholder="Укажите сумму"
              :formatter="costsFormatter"
              :parser="costsParser"
              disabled
            />
          </div>
          <div class="pc-created-cell">
            <span class="ms-help label"> Дата платежа </span>
            <date-picker
              size="default"
              format="dd.MM.yyyy"
              placeholder="Выберите дату"
              v-model="payment.date"
              :options="dateOptions"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="pc-created-control">
      <i-button type="default" size="default" long @click="cancelNewPayment">
        Отменить
      </i-button>
      <i-button
        type="success"
        size="default"
        long
        @click="createNewPayment"
        :disabled="!allInfoFilled"
        :loading="saving"
      >
        Создать платеж
      </i-button>
    </div>
  </card>
</template>

<script>
import moment from 'moment'
import PaymentsEditorRoadmap from '@/components/payments/editor/payments-editor-roadmap'
import { getFormattedCosts, costsFormatter, costsParser } from '@/utils'
import { paymentTypes as types } from '@/config/payments'

export default {
  components: {
    PaymentsEditorRoadmap
  },
  props: {
    payment: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      saving: false,
      companies: [],
      acts: [],
      roadmap: [],
      dateOptions: {
        disabledDate: (date) => {
          const start = new Date()

          return date && date.valueOf() > start.valueOf()
        }
      },
      types
    }
  },
  computed: {
    activePayment () {
      return this.$store.state.paymentsControl.activePayment
    },
    currentMaxValue () {
      const { act_id } = this.payment
      const paymentMaxValue =
        this.activePayment.costs - this.activePayment.distributed_costs

      if (!act_id) {
        return paymentMaxValue
      }

      const act = this.acts.find((a) => a.id === this.payment.act_id)

      if (!act) {
        return paymentMaxValue
      }

      if (paymentMaxValue < act.debt_payments) return paymentMaxValue

      return act.debt_payments
    },
    contracts () {
      return this.$store.state.paymentsControl.contracts || []
    },
    commonInfoFilled () {
      const { contract_id, from_company_id, to_company_id } = this.payment

      return contract_id && from_company_id && to_company_id
    },
    allInfoFilled () {
      const {
        contract_id,
        from_company_id,
        to_company_id,
        date,
        value,
        type_id
      } = this.payment

      return (
        contract_id &&
        from_company_id &&
        to_company_id &&
        date &&
        value &&
        type_id
      )
    }
  },
  methods: {
    getActLabel ({ number, payments, costs }) {
      return `Акт №${number} (оплачено ${payments} / ${costs})`
    },
    async getContractData () {
      try {
        // get and set companies
        const companiesUrl = `page/other_company`
        const companies = await this.$store.dispatch('GET_REQUEST', {
          url: companiesUrl
        })
        const ownCompanies = this.$store.state.companies

        this.companies = [...ownCompanies, ...companies]

        // if (!this.payment.from_company_id) {
        //   console.log('id --- ', this.payment.contract_id)
        //   const contract = this.contracts.find(c => c.id === this.payment.contract_id)
        //   this.$set(this.payment, 'from_company_id', contract.customer_id)
        // }

        // get and set acts
        const actsUrl = `page/act?contract_id=${this.payment.contract_id}`
        this.acts = await this.$store.dispatch('GET_REQUEST', {
          url: actsUrl
        })

        // get roadmap
        const roadmapUrl = `page/roadmap_act_cost_lag/${this.payment.contract_id}`

        this.roadmap = await this.$store.dispatch('GET_REQUEST', {
          url: roadmapUrl
        })
      } catch (error) {
        console.log(error)
      }
    },
    cancelNewPayment () {
      this.$emit('cancel')
    },
    async createNewPayment () {
      try {
        this.saving = true

        const url = 'page/payment'
        const data = {
          ...this.payment,
          date: moment(this.payment.date).format(),
          payment_accounting_id: this.activePayment.id,
          roadmap_payments: this.payment.roadmap_payments.map((r) => ({
            id: r.id,
            roadmap_id: r.roadmap_id,
            value: r.value,
            disabled: r.disabled
          }))
        }

        await this.$store.dispatch('POST_REQUEST', {
          url,
          data
        })
        this.$emit('save')

        this.saving = false
      } catch (error) {
        console.log(error)
        this.saving = false
      }
    },
    getFormattedCosts,
    costsFormatter,
    costsParser
  }
}
</script>

<style lang="less">
.pc-created {
  .ivu-divider {
    margin: 0 !important;
  }

  &-row {
    display: flex;
    align-items: center;

    &:not(:first-child) {
      margin-top: 8px;
    }
  }

  &-cell {
    flex: 1;

    &:not(:first-child) {
      margin-left: 16px;
    }

    .ivu-date-picker,
    .ivu-input-number {
      width: 100%;
    }
  }

  &-details {
    margin-top: 16px;
  }

  &-subinfo {
    margin-top: 16px;
  }

  &-act-option {
    margin-left: 8px;
  }

  &-control {
    margin-top: 24px;
    display: flex;

    .ivu-btn:not(:first-child) {
      margin-left: 16px;
    }
  }

  &-companies-dropdown {
    max-width: 788px;

    .ivu-select-item {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
